import { apiClient, APIResponse, PaginatedResponse, QueryOptions } from './client';

export interface Project {
  id: number;
  workgroupid: number;
  applicationid: number;
  name: string;
  repoid?: string;
  repousername?: string;
  repoprovider?: string;
  repourl?: string;
  reponame?: string;
  repotype?: string;
  // The ciphertext is never returned by the API -- use this flag for "GitHub connected"
  // checks instead. A fresh plaintext token is only ever available right after the
  // OAuth callback or a manual paste, never read back off a project response.
  hasrepoaccesstoken?: boolean;
  technology?: string;
  description?: string;
  isfavourite: boolean;
  isarchived: boolean;
  isactive: boolean;
  isdeleted: boolean;
  createdon: string;
  createdby: number;
  lastactivityon?: string;
  lastactivityby?: number;

  application_name?: string;
  applicationtype?: string;
  application_business_criticality?: string;

  creator_firstname?: string;
  creator_lastname?: string;
  creator_emailid?: string;
  creator_designation?: string;
  creator_department?: string;

  total_scan_count?: number;
  completed_scan_count?: number;
  pending_scan_count?: number;
  running_scan_count?: number;
  failed_scan_count?: number;
  last_scan_date?: string;
  last_completed_scan_date?: string;
  first_scan_date?: string;

  total_component_count?: number;
  unique_component_count?: number;
  latest_component_count?: number;
  encryptioncomponents?: number;
  provenancecomponents?: number;

  total_vulnerabilities?: number;
  critical_vulnerabilities?: number;
  high_vulnerabilities?: number;
  medium_vulnerabilities?: number;
  low_vulnerabilities?: number;

  total_license_issues?: number;
  critical_license_issues?: number;
  high_license_issues?: number;
  medium_license_issues?: number;
  low_license_issues?: number;

  avg_overall_score?: number;
  avg_vulnerability_score?: number;
  avg_license_score?: number;
  avg_operational_risk_score?: number;
  avg_technical_debt_score?: number;
  avg_business_risk_score?: number;

  latest_overall_score?: number;
  latest_vulnerability_score?: number;
  latest_license_score?: number;
  latest_operational_risk_score?: number;
  latest_technical_debt_score?: number;
  encryption_count?: number;
  provenance_count?: number;
  risk_level?: string;
  status?: string;

  applications?: any;
  scans?: any[];
}

export interface CreateProjectData {
  workgroupid: number;
  applicationid: number;
  name: string;
  repoid?: string;
  repousername?: string;
  repoprovider?: string;
  repourl?: string;
  reponame?: string;
  repotype?: string;
  repoaccesstoken?: string;
  // Server copies the token from the caller's profile (set by GitHub OAuth connect).
  useprofilegithubtoken?: boolean;
  technology?: string;
  description?: string;
}

export interface UpdateProjectData extends Partial<CreateProjectData> {
  isfavourite?: boolean;
  isarchived?: boolean;
}

export interface FindProjectsOptions extends QueryOptions {
  applicationId?: number;
  applicationIds?: number[]; 
}

export interface ProjectSecurityMetrics {
  projectId: number;
  projectName: string;
  totalVulnerabilities: number;
  criticalVulnerabilities: number;
  highVulnerabilities: number;
  mediumVulnerabilities: number;
  lowVulnerabilities: number;
  totalLicenseIssues: number;
  highLicenseIssues: number;
  mediumLicenseIssues: number;
  lowLicenseIssues: number;
  riskScore: number;
  lastScanDate: string;
}

export interface ProjectLicenseDistribution {
  licenseName: string;
  componentCount: number;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  percentage: number;
}

export class ProjectAPI {
  static async getAll(options?: FindProjectsOptions): Promise<APIResponse<PaginatedResponse<Project>>> {
    return apiClient.get<PaginatedResponse<Project>>('/projects', options);
  }

  static async getList(applicationId?: number): Promise<APIResponse<Project[]>> {
    return apiClient.get<Project[]>(`/projects/findList${applicationId ? `/${applicationId}` : ''}`);
  }
  static async getById(id: number): Promise<APIResponse<Project>> {
    return apiClient.get<Project>(`/projects/${id}`);
  }

  static async create(data: CreateProjectData): Promise<APIResponse<Project>> {
    return apiClient.post<Project>('/projects', data);
  }

  static async update(id: number, data: UpdateProjectData): Promise<APIResponse<Project>> {
    return apiClient.patch<Project>(`/projects/${id}`, data);
  }


  static async updateRepo(id: number, data: UpdateProjectData): Promise<APIResponse<Project>> {
    return apiClient.patch<Project>(`/projects/repo/${id}`, data);
  }

  static async updateRepoFromProfile(id: number, data: { repoid?: string }) {
    return apiClient.post<{ updated: boolean; repousername: string }>(`/projects/${id}/repo/from-profile`, data);
  }

  static async delete(id: number): Promise<APIResponse<void>> {
    return apiClient.delete<void>(`/projects/${id}`);
  }

  static async archive(id: number): Promise<APIResponse<void>> {
    return apiClient.delete<void>(`/projects/archive/${id}`);
  }

  static async restore(id: number): Promise<APIResponse<void>> {
    return apiClient.patch<void>(`/projects/restore/${id}`);
  }

  static async favourite(id: number, isfavourite: boolean): Promise<APIResponse<void>> {
    return apiClient.patch<void>(`/projects/favourite/${id}/${isfavourite}`);
  }


  static async getApplications(id: number): Promise<APIResponse<any[]>> {
    return apiClient.get<any[]>(`/projects/${id}/applications`);
  }

  static async getScans(id: number): Promise<APIResponse<any[]>> {
    return apiClient.get<any[]>(`/projects/${id}/scans`);
  }

}
